import { useEffect } from 'react';
import { ILightboxProps } from '../../types/lightbox.types';
import { useLightbox } from '../../hooks/useLightbox';
import { InnerButton } from '@/modules/InnerButton';
import { faAngleLeft, faAngleRight, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';

export const Lightbox = ({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
  displayMode = 'fullWidth',
  showTitle = true,
  showIndicators = true,
  showNavigation = true,
  showCounter = false,
  className = '',
  overlayClassName = '',
  size = 'md',
}: ILightboxProps) => {
  const { currentIndex, currentImage, goToPrevious, goToNext, goToImage, hasPrevious, hasNext } =
    useLightbox({ images, initialIndex, onClose });

  // Bloquear scroll del body cuando el lightbox está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !currentImage) return null;

  const aspectClass = displayMode === 'square' ? 'ui-aspect-square' : 'ui-aspect-video';

  const sizeClass = {
    sm: 'ui-w-[512px]',
    md: 'ui-w-[896px]',
    lg: 'ui-w-[1152px]',
    full: 'ui-w-full',
  };

  return (
    <div
      className={cn(
        'ui-imj ui-fixed ui-inset-0 ui-z-[52] ui-flex ui-items-center ui-justify-center',
        overlayClassName
      )}
      onClick={onClose}
    >
      {/* Overlay oscuro */}
      <div className="opacity-50 ui-absolute ui-inset-0 ui-bg-[#00000080] ui-backdrop-opacity-50" />

      {/* Contenedor principal */}
      <div
        className={cn(
          'ui-relative ui-z-10 ui-flex ui-max-h-[90vh] ui-w-full ui-flex-col ui-items-center ui-justify-center ui-gap-4 ui-px-4 ui-py-8',
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Contenedor de imagen y navegación */}
        <div className="ui-relative ui-flex ui-w-full ui-flex-1 ui-items-center ui-justify-center ui-gap-6 ui-overflow-hidden">
          {/* Botón anterior */}
          {showNavigation && hasPrevious && (
            <InnerButton
              theme="solid"
              color="secondary"
              size="sm"
              pill="pill"
              iconProps={{ icon: faAngleLeft }}
              onClick={goToPrevious}
              tooltipProps={{ label: 'Anterior', position: 'right' }}
              className="ui-bg-fill/90 ui-flex-shrink-0 ui-shadow-04 hover:ui-bg-fill"
            />
          )}

          {/* Contenedor de imagen */}
          <div
            className={cn(
              aspectClass,
              sizeClass[size],
              'ui-relative ui-max-h-[70vh] ui-max-w-[90vw] ui-overflow-hidden ui-rounded-lg ui-shadow-2xl'
            )}
          >
            <img
              src={currentImage.src}
              alt={currentImage.title || `Imagen ${currentIndex + 1}`}
              className="ui-absolute ui-inset-0 ui-h-full ui-w-full ui-object-cover"
            />

            {/* Botón cerrar - dentro de la imagen */}
            <div className="ui-absolute ui-right-4 ui-top-4 ui-z-20">
              <InnerButton
                theme="solid"
                color="secondary"
                size="sm"
                pill="pill"
                iconProps={{ icon: faXmark }}
                onClick={onClose}
                tooltipProps={{ label: 'Cerrar', position: 'bottom' }}
                className="ui-bg-fill/90 ui-shadow-04 hover:ui-bg-fill"
              />
            </div>
          </div>

          {/* Botón siguiente */}
          {showNavigation && hasNext && (
            <InnerButton
              theme="solid"
              color="secondary"
              size="sm"
              pill="pill"
              iconProps={{ icon: faAngleRight }}
              onClick={goToNext}
              tooltipProps={{ label: 'Siguiente', position: 'left' }}
              className="ui-bg-fill/90 ui-flex-shrink-0 ui-shadow-04 hover:ui-bg-fill"
            />
          )}
        </div>

        {/* Título */}
        {showTitle && currentImage.title && (
          <div className="ui-bg-black/50 ui-rounded-lg ui-px-4 ui-py-2 ui-text-center ui-text-lg ui-font-medium ui-text-primary ui-backdrop-blur-sm">
            {currentImage.title}
          </div>
        )}

        {/* Indicadores (círculos) */}
        {showIndicators && images.length > 1 && (
          <div className="ui-bg-black/50 ui-flex ui-items-center ui-gap-2 ui-rounded-control-pill ui-px-4 ui-py-3 ui-backdrop-blur-sm">
            {images.map((image, index) => (
              <button
                key={image.id}
                onClick={() => goToImage(index)}
                className={`ui-h-2.5 ui-w-2.5 ui-rounded-control-pill ui-border-1 ui-transition-all ui-duration-300 ${
                  index === currentIndex
                    ? 'ui-border-white ui-bg-fill-brand'
                    : 'ui-border-default ui-bg-fill hover:ui-border-white hover:ui-bg-fill-brand'
                }`}
                aria-label={`Ir a imagen ${index + 1}`}
                title={image.title || `Imagen ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Contador de imágenes */}
        {showCounter && (
          <div className="ui-bg-black/50 ui-absolute ui-bottom-4 ui-left-4 ui-rounded-control-pill ui-px-3 ui-py-1 ui-text-sm ui-font-medium ui-text-white ui-backdrop-blur-sm">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};
